<template>
  <div>
    <div class="floating-buttons-container" :style="{ [verticalPosition]: finalPosition, [horizontalPosition]: `${this.xMargin}px` }">
      <button :class="['btn', 'floating-button', `floating-button-${buttonSelectedColor}`]" @click="handleClickButton()">
        <i :class="['fa-solid', iconClass]"></i>
      </button>
      <button v-if="usePanelBadge && allowHidePanelBadge" :class="['badge', `badge-main`, 'badge-sm', 'mini-button']" :style="miniButtonStyle" @click="handleClickMiniButton()" :title="(showPanelBadge ? 'Hide' : 'Show') + ' message'">
        <i :class="['fa-solid', iconClassMini]"></i>
      </button>
    </div>
    <div v-if="usePanelContent && alternatePanel && showPanelContent" :class="['panel', 'panel-content', `panel-${panelSelectedColor}`]" :style="panelContentStyle">
      <div class="innerblock" style="opacity: 0.9">
        <slot name="panel-content"> Fallback Text </slot>
      </div>
    </div>
    <div v-if="usePanelBadge && !alternatePanel && showPanelBadge" :class="['panel', 'panel-badge', `panel-${panelSelectedColor}`]" :style="panelBadgeStyle">
      <div class="innerblock" style="opacity: 0.75">
        <slot name="panel-badge"> Fallback Text </slot>
      </div>
    </div>
  </div>
</template>

<script>
import { footerHeight } from '@states/FooterState';
import { headerHeight } from '@states/HeaderState';
export default {
  name: 'GlobalAlert',
  emits: ['click'],
  props: {
    usePanelContent: {
      type: Boolean,
      default: false,
    },
    usePanelBadge: {
      type: Boolean,
      default: false,
    },
    allowHidePanelBadge: {
      type: Boolean,
      default: true,
    },
    buttonSelectedColor: {
      type: String,
      default: 'main',
      validator: (value) => ['main', 'yellow', 'green', 'red', 'gray'].includes(value),
    },
    panelSelectedColor: {
      type: String,
      default: 'main',
      validator: (value) => ['main', 'yellow', 'green', 'red', 'gray'].includes(value),
    },
    iconClass: {
      type: String,
      default: 'fa-trash',
    },
    verticalPosition: {
      type: String,
      default: 'bottom',
      validator: (value) => ['top', 'bottom'].includes(value),
    },
    horizontalPosition: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'right'].includes(value),
    },
    xMargin: {
      type: Number,
      default: 20,
    },
    yMargin: {
      type: Number,
      default: 20,
    },
  },
  setup() {
    return { footerHeight, headerHeight };
  },
  data() {
    return {
      alternatePanel: false,
      showPanelContent: true,
      showPanelBadge: true,
      iconClassMini: 'fa-eye',
    };
  },
  computed: {
    finalPosition() {
      if (this.verticalPosition == 'bottom') {
        return `calc(${this.yMargin}px + ${this.footerHeight})`;
      } else {
        return `calc(${this.yMargin}px + ${this.headerHeight})`;
      }
    },
    miniButtonStyle() {
      const diagonalGap = 35;
      const styles = {};
      // Estilos Verticales
      if (this.verticalPosition === 'bottom') {
        styles.top = `${diagonalGap}px`;
      } else {
        styles.bottom = `${diagonalGap}px`;
      }
      // Estilos Horizontales
      if (this.horizontalPosition === 'right') {
        styles.left = `${diagonalGap}px`;
      } else {
        styles.right = `${diagonalGap}px`;
      }
      return styles;
    },
    panelContentStyle() {
      const diagonalGap = 40;
      const styles = {};
      // Estilos Verticales
      if (this.verticalPosition === 'bottom') {
        styles.bottom = `calc(${this.yMargin + diagonalGap}px + ${this.footerHeight})`;
      } else {
        styles.top = `calc(${this.yMargin + diagonalGap}px + ${this.headerHeight})`;
      }
      // Estilos Horizontales
      if (this.horizontalPosition === 'right') {
        styles.right = `calc(${this.xMargin + diagonalGap}px)`;
      } else {
        styles.left = `calc(${this.xMargin + diagonalGap}px)`;
      }
      return styles;
    },
    panelBadgeStyle() {
      const horizontalGap = 45;
      const styles = {};
      // Estilos Verticales
      if (this.verticalPosition === 'bottom') {
        styles.bottom = `calc(${this.yMargin}px + ${this.footerHeight})`;
      } else {
        styles.top = `calc(${this.yMargin}px + ${this.headerHeight})`;
      }
      // Estilos Horizontales
      if (this.horizontalPosition === 'right') {
        styles.right = `calc(${this.xMargin + horizontalGap}px)`;
        styles.clipPath = 'polygon(0% 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 0% 100%)';
      } else {
        styles.left = `calc(${this.xMargin + horizontalGap}px)`;
        styles.clipPath = 'polygon(10px 0%, 100% 0%, 100% 100%, 10px 100%, 0% 50%)';
      }
      return styles;
    },
  },
  methods: {
    openPanel() {
      this.alternatePanel = true;
      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      });
    },
    closePanel(promiseResolution = true) {
      this.alternatePanel = false;
      if (this.resolvePromise) {
        this.resolvePromise(promiseResolution);
        this.resolvePromise = null;
        this.rejectPromise = null;
      }
    },
    handleClickButton() {
      this.$emit('click');
      this.alternatePanel = !this.alternatePanel;
    },
    handleClickMiniButton() {
      if (this.showPanelBadge) {
        this.iconClassMini = 'fa-eye-slash';
        this.showPanelBadge = false;
      } else {
        this.iconClassMini = 'fa-eye';
        this.showPanelBadge = true;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@mixin btn-floating-variant($name, $main, $soft, $light, $dark) {
  &.floating-button-#{$name} {
    background-color: $main;
    color: var(--Light);
    border: 0.5px solid var(--Light);

    &:hover {
      background-color: $light;
      box-shadow: 0 0 20px rgba($soft, 1), 0 0 40px rgba($soft, 0.8);
      transform: scale(1.1);
    }

    &:active {
      transform: scale(1);
    }

    i {
      font-size: 110%;
      line-height: 50px;
      text-shadow: 0 0 5px rgba($dark, 1), 0 0 10px rgba($dark, 0.7);
    }
  }
}

.floating-buttons-container {
  z-index: 100;
  position: fixed;
}

.floating-button {
  z-index: 1;
  width: 50px;
  height: auto;
  padding: 0;
  font-size: 18px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  @include btn-floating-variant('main', var(--Sky), var(--Soft-Sky), var(--Midnight-Sky), var(--Dark-Sky));
  @include btn-floating-variant('red', var(--Persian-Red), var(--Jasper-Red), var(--Light-Red), var(--Dark-Red));
  @include btn-floating-variant('yellow', var(--Yellow), var(--Soft-Yellow), var(--Light-Yellow), var(--Dark-Yellow));
  @include btn-floating-variant('green', var(--Asparagus-Green), var(--Olive-Green), var(--Light-Green), var(--Dark-Green));
  @include btn-floating-variant('gray', var(--Gray), var(--Soft-Gray), var(--Light), var(--Dark-Gray));
}

@mixin panel-variant($name, $main, $soft, $light, $dark) {
  &.panel-#{$name} {
    .innerblock {
      background-color: var(--Light);
      border: 3px solid $main;
      border-radius: 5px;
    }
  }
}

.panel {
  position: fixed;
  z-index: 101;
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;
  opacity: 1;

  &.hide {
    transform: scale(0.8);
    opacity: 0;
  }

  .innerblock {
    max-height: 420px;
    overflow: hidden;
    backdrop-filter: blur(4px);
    padding: 12px;
  }

  &-content {
    min-width: 100px;
    min-height: 50px;
  }

  &-badge {
    min-width: 100px;
    min-height: 20px;
  }

  // Variantes de panel
  @include panel-variant('main', var(--Sky), var(--Soft-Sky), var(--Highlight-Sky), var(--Dark-Sky));
  @include panel-variant('red', var(--Persian-Red), var(--Jasper-Red), var(--Light-Red), var(--Dark-Red));
  @include panel-variant('yellow', var(--Yellow), var(--Soft-Yellow), var(--Light-Yellow), var(--Dark-Yellow));
  @include panel-variant('green', var(--Asparagus-Green), var(--Olive-Green), var(--Light-Green), var(--Dark-Green));
  @include panel-variant('gray', var(--Gray), var(--Soft-Gray), var(--Light), var(--Dark-Gray));
}

.mini-button {
  position: absolute;
  z-index: 2;
  width: 20px;
  height: 20px;
  border: 0.5px solid var(--Light);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;

  i {
    font-size: 11px;
  }
}
</style>
